/**
 * thermal-printer.js — Web Bluetooth integration for thermal label printers.
 *
 * Supports two protocols:
 *   - ESC/POS (most common — Star, Epson, generic Bluetooth thermal printers)
 *   - ZPL (Zebra label printers)
 *
 * Usage:
 *   import { connectPrinter, printLabel, isPrinterSupported } from "../lib/thermal-printer";
 *   const printer = await connectPrinter();
 *   await printLabel(printer, { booking_number, customer_name, pickup_code, qr_text, ... });
 *
 * Browser support:
 *   - Chrome desktop + Chrome Android (Web Bluetooth API)
 *   - NOT iOS Safari (no Web Bluetooth)
 *   - Fallback: download PDF + use browser print dialog
 */

// Common BLE service UUIDs used by thermal printers
const BLE_SERVICES = [
  "000018f0-0000-1000-8000-00805f9b34fb", // generic Bluetooth printer
  "0000ff00-0000-1000-8000-00805f9b34fb", // Goojprt / many cheap thermal printers
  "0000ffe0-0000-1000-8000-00805f9b34fb", // HC-05 / serial-over-BLE
  "0000ffb0-0000-1000-8000-00805f9b34fb", // Pos-58
];

const BLE_CHARACTERISTICS = [
  "00002af1-0000-1000-8000-00805f9b34fb", // generic printer characteristic
  "0000ff01-0000-1000-8000-00805f9b34fb",
  "0000ff02-0000-1000-8000-00805f9b34fb",
  "0000ffe1-0000-1000-8000-00805f9b34fb",
  "0000ffb1-0000-1000-8000-00805f9b34fb",
];

const ESC = 0x1b;
const GS = 0x1d;

export const isPrinterSupported = () =>
  typeof navigator !== "undefined" && !!navigator.bluetooth;

export async function connectPrinter() {
  if (!isPrinterSupported()) {
    throw new Error("Web Bluetooth ikke understøttet i denne browser. Brug Chrome på Android eller desktop.");
  }
  const device = await navigator.bluetooth.requestDevice({
    acceptAllDevices: true,
    optionalServices: BLE_SERVICES,
  });
  const server = await device.gatt.connect();
  // Try each common service/characteristic combo until one works
  let writeChar = null;
  for (const svcUuid of BLE_SERVICES) {
    try {
      const svc = await server.getPrimaryService(svcUuid);
      for (const charUuid of BLE_CHARACTERISTICS) {
        try {
          const ch = await svc.getCharacteristic(charUuid);
          if (ch.properties.write || ch.properties.writeWithoutResponse) {
            writeChar = ch;
            break;
          }
        } catch { /* try next */ }
      }
      if (writeChar) break;
    } catch { /* try next service */ }
  }
  if (!writeChar) {
    throw new Error("Kunne ikke finde et skriv-characteristic på printeren. Tjek at det er en ESC/POS-kompatibel thermal-printer.");
  }
  return { device, server, writeChar, name: device.name || "Thermal Printer" };
}

const enc = new TextEncoder();
// Write bytes in chunks (BLE has small MTU, typically 20-244 bytes)
async function writeChunked(char, bytes, chunkSize = 100) {
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.slice(i, i + chunkSize);
    if (char.properties.writeWithoutResponse) {
      await char.writeValueWithoutResponse(chunk);
    } else {
      await char.writeValue(chunk);
    }
    // small delay so the printer buffer can catch up
    await new Promise((r) => setTimeout(r, 30));
  }
}

function buildEscPosLabel({ booking_number, customer_name, customer_phone, pickup_code, shop_name, qr_data, service_type, recipient_name, recipient_phone, is_return = false }) {
  const parts = [];
  parts.push(new Uint8Array([ESC, 0x40])); // INIT
  parts.push(new Uint8Array([ESC, 0x61, 0x01])); // CENTER
  // Title
  parts.push(new Uint8Array([GS, 0x21, 0x11])); // 2x size
  parts.push(enc.encode((is_return ? "EMA RETUR" : "EMA PICKUP") + "\n"));
  parts.push(new Uint8Array([GS, 0x21, 0x00])); // normal
  parts.push(enc.encode("\n"));
  // QR code via ESC/POS GS ( k command
  if (qr_data) {
    const qrText = qr_data;
    const qrBytes = enc.encode(qrText);
    // Set model
    parts.push(new Uint8Array([GS, 0x28, 0x6b, 4, 0, 49, 65, 50, 0]));
    // Module size (1..16) — 8 is large/readable
    parts.push(new Uint8Array([GS, 0x28, 0x6b, 3, 0, 49, 67, 8]));
    // Error correction level
    parts.push(new Uint8Array([GS, 0x28, 0x6b, 3, 0, 49, 69, 49]));
    // Store data
    const len = qrBytes.length + 3;
    parts.push(new Uint8Array([GS, 0x28, 0x6b, len & 0xff, (len >> 8) & 0xff, 49, 80, 48]));
    parts.push(qrBytes);
    // Print
    parts.push(new Uint8Array([GS, 0x28, 0x6b, 3, 0, 49, 81, 48]));
  }
  parts.push(enc.encode("\n"));
  // Booking number
  parts.push(new Uint8Array([GS, 0x21, 0x10])); // tall
  parts.push(enc.encode(`#${booking_number || "?"}\n`));
  parts.push(new Uint8Array([GS, 0x21, 0x00]));
  parts.push(enc.encode("\n"));
  // Left-align body
  parts.push(new Uint8Array([ESC, 0x61, 0x00])); // LEFT
  parts.push(enc.encode("AFLEVERES HOS:\n"));
  parts.push(new Uint8Array([ESC, 0x45, 0x01])); // BOLD on
  parts.push(enc.encode(`${(shop_name || "").slice(0, 32)}\n`));
  parts.push(new Uint8Array([ESC, 0x45, 0x00])); // BOLD off
  parts.push(enc.encode("\nMODTAGER:\n"));
  parts.push(new Uint8Array([ESC, 0x45, 0x01]));
  parts.push(enc.encode(`${(recipient_name || customer_name || "").slice(0, 32)}\n`));
  parts.push(new Uint8Array([ESC, 0x45, 0x00]));
  if (recipient_phone || customer_phone) {
    parts.push(enc.encode(`${recipient_phone || customer_phone}\n`));
  }
  if (pickup_code) {
    parts.push(enc.encode("\nPICKUP-KODE:\n"));
    parts.push(new Uint8Array([GS, 0x21, 0x22])); // 3x size
    parts.push(enc.encode(`${pickup_code}\n`));
    parts.push(new Uint8Array([GS, 0x21, 0x00]));
  }
  if (service_type) {
    parts.push(enc.encode(`\nServicetype: ${service_type.toUpperCase()}\n`));
  }
  parts.push(enc.encode("\nEMA Kurer ApS · CVR 46465717\n"));
  // Feed paper & cut
  parts.push(enc.encode("\n\n\n"));
  parts.push(new Uint8Array([GS, 0x56, 0x42, 0x00])); // partial cut
  // Concat
  const total = parts.reduce((n, p) => n + p.length, 0);
  const buf = new Uint8Array(total);
  let off = 0;
  for (const p of parts) {
    buf.set(p, off);
    off += p.length;
  }
  return buf;
}

export async function printLabel(printer, labelData) {
  if (!printer || !printer.writeChar) throw new Error("Printer ikke forbundet");
  const bytes = buildEscPosLabel(labelData);
  await writeChunked(printer.writeChar, bytes);
}

export async function disconnectPrinter(printer) {
  try {
    if (printer?.server?.connected) {
      printer.server.disconnect();
    }
  } catch { /* ignore */ }
}
